import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/**
 * `/forgot-password` — asks for a reset link.
 *
 * The screen deliberately says the **same thing whether or not the address has
 * an account**, because the API deliberately does (`ForgotPasswordResponse`:
 * "says nothing about whether the address exists"). A page that said "no such
 * account" would undo that in one line and turn the form into an oracle for
 * enumerating customers.
 *
 * Rate limiting lives on the server, and its refusal is `RATE_LIMITED` through
 * the ordinary envelope — so the inline error shows it without this page
 * knowing the policy.
 */
export function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)

  const request = useMutation((body: { email: string }) =>
    call(() => api.POST('/api/v1/auth/password/forgot', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const address = email.trim().toLowerCase()
    const result = await request.run({ email: address })
    if (result.ok) setSentTo(address)
  }

  if (sentTo) {
    return (
      <PublicPage
        title="Check your email"
        footer={
          <>
            Wrong address?{' '}
            <button
              type="button"
              className="ar-link"
              style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}
              onClick={() => {
                setSentTo(null)
                request.reset()
              }}
            >
              Try another one
            </button>
          </>
        }
      >
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            If <strong>{sentTo}</strong> has an AugRank account, a reset link is on its way. It
            works once and expires.
          </p>
          <p className="ar-muted">
            Nothing after a few minutes? Check spam, and confirm you typed the address you signed up
            with.
          </p>
          <Link className="ar-btn ar-btn--ghost" to="/login">
            Back to sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Reset your password"
      footer={
        <>
          Remembered it? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <InlineError failure={request.state.failure} />
          <TextField
            label="Email address"
            type="email"
            required
            autoFocus
            autoComplete="email"
            help="The address you signed up with."
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              request.reset()
            }}
          />
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="primary" type="submit" busy={request.pending} busyLabel="Sending">
            Send reset link
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}
